import { AI_FEATURES, AI_RUN_STATUSES } from '@sahab/contracts'
import { z } from 'zod'

/**
 * Settings › AI assistant › Usage and runs: the month and the runs' filters live in the URL
 * (shareable, back-button safe); an invalid value falls back to the default.
 */
export const aiUsageSearchSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  status: z.enum(AI_RUN_STATUSES).optional().catch(undefined),
  feature: z.enum(AI_FEATURES).optional().catch(undefined),
  userId: z.uuid().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
})
export type AiUsageSearch = z.infer<typeof aiUsageSearchSchema>

/** `YYYY-MM` of the calendar month in Muscat, `back` months before `now`. */
export function muscatMonth(now: Date, back = 0): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Muscat',
    year: 'numeric',
    month: '2-digit',
  })
    .formatToParts(now)
    .reduce<Record<string, string>>((acc, p) => Object.assign(acc, { [p.type]: p.value }), {})
  const index = Number(parts.year) * 12 + Number(parts.month) - 1 - back
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
}

/** The first and last day of a `YYYY-MM` month (`from`/`to` of the runs list). */
export function monthRange(month: string): { from: string; to: string } {
  const [year, mon] = month.split('-').map(Number) as [number, number]
  const last = new Date(Date.UTC(year, mon, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

/** How many months back the month picker reaches (run metadata is kept 24 months). */
export const USAGE_MONTHS = 12
